import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import QRCode from "qrcode";
import FBRLogo from "../../Images/fbrDigitalInvoicingLogo.jpg";
import {
  displayTwoDecimalDigits,
  section236HAmountFor,
  section236HFinalAmountFor,
  section236HLabel,
  SECTION_236H_FINAL_LABEL,
  hasSection236H,
} from "../../global/Utils";
import { formatTwoDecimals } from "../../global/money";
import { sellerPhoneRows } from "../../global/sellerPhones";
import { toWords } from "number-to-words";
import moment from "moment";

// ─── "Half Page Invoice" pattern ────────────────────────────────────────────
// The Default invoice's design scaled down to half an A4 sheet, selected per
// company via Company.invoicePattern. The PDF is an A4 portrait page with the
// invoice in its top half (at most 210 x 148.5 mm) and the rest left blank, so
// printing it on ordinary A4 paper at 100% puts the invoice on the top half.
// A short invoice ends right under its "computer generated" note, with the
// footer bar there instead of at the foot of the half page.
//
// It carries everything the Default invoice does - logo, 1-inch QR, seller /
// purchaser / invoice details, all 19 item columns, summary with Discount and
// 236H, amount in words - at smaller type. An invoice with too many items for
// one half continues in the top half of the next sheet, under a slim
// "continued" strip, with the table header repeated and no row split in two.

// Same palette as the Default template (generateInvoice.js)
const P = {
  navy: [26, 40, 79], // #1a284f
  blue: [58, 128, 251], // #3A80FB
  lightBlue: [235, 242, 250], // soft blue tint
  white: [255, 255, 255],
  offWhite: [247, 249, 252],
  border: [220, 228, 240],
  textDark: [30, 41, 59],
  textMuted: [100, 116, 139],
  blueFade: [170, 198, 240], // muted text on dark bg
  grandTotalBg: [42, 105, 210], // slightly deeper blue for grand total row
};

/** Builds the half-page invoice PDF and returns the jsPDF document. */
export const buildHalfPageInvoice = async (data) => {
  const doc = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
  // Ask the viewer to print at actual size instead of fitting the page to the
  // paper, so the invoice keeps its exact half-sheet size.
  doc.viewerPreferences({ PrintScaling: "None" });

  const PW = doc.internal.pageSize.getWidth(); // 210 mm
  const SHEET_H = doc.internal.pageSize.getHeight(); // 297 mm
  const PH = SHEET_H / 2; // 148.5 mm - the invoice is laid out in this half
  const M = 8; // left / right margin
  const FOOTER_BAR_H = 6;
  const CONTENT_BOTTOM = PH - FOOTER_BAR_H - 3; // max y before footer
  const CONT_BAND_H = 8; // slim header strip on continuation pages
  const CONT_TOP = CONT_BAND_H + 1 + 3; // where content resumes on page 2+

  let y = 0;

  const companyName = data?.companyId?.companyName || "";
  const invoiceRef = data?.FBRInvoiceRefNo || "";
  const invoiceNo = data?.invoiceNumber || "";

  // ─── Drawing helpers ─────────────────────────────────────────────────────

  /** Filled rectangle (no border) */
  const fillRect = (x, ry, w, h, color) => {
    doc.setFillColor(...color);
    doc.rect(x, ry, w, h, "F");
  };

  /** Stroked rectangle, optional fill */
  const strokeRect = (x, ry, w, h, borderColor, bgColor = null) => {
    doc.setDrawColor(...borderColor);
    if (bgColor) {
      doc.setFillColor(...bgColor);
      doc.rect(x, ry, w, h, "FD");
    } else {
      doc.rect(x, ry, w, h, "S");
    }
  };

  /** Horizontal rule */
  const hLine = (lx, rx, ry, color = P.border) => {
    doc.setDrawColor(...color);
    doc.line(lx, ry, rx, ry);
  };

  /** Add a new page and reset y below its continuation strip */
  const newPage = () => {
    doc.addPage();
    y = CONT_TOP;
  };

  const needsNewPage = (h) => {
    if (y + h > CONTENT_BOTTOM) {
      newPage();
      return true;
    }
    return false;
  };

  // ─── HEADER BAND ─────────────────────────────────────────────────────────
  // The QR keeps the Default invoice's 25 mm (about 1 inch) so it scans the
  // same; "Scan QR code to verify" sits beside it instead of under it to save
  // height.
  const HDR_H = 29;
  const QR_S = 25;
  const QR_X = PW - M - QR_S;
  const PILL_W = 32;
  const PILL_X = QR_X - 5 - PILL_W;

  // Navy background
  fillRect(0, 0, PW, HDR_H, P.navy);

  // Bottom accent stripe
  fillRect(0, HDR_H - 1.5, PW, 1.5, P.blue);

  // FBR Logo (left)
  doc.addImage(FBRLogo, "JPG", M, 1.5, 25, 25);

  // Title – centered
  doc.setFont("Helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(...P.white);
  doc.text("Sale Tax Invoice", PW / 2, 12.5, { align: "center" });

  // Sub-label
  doc.setFont("Helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...P.blueFade);
  doc.text("FBR Digital Sales Tax Invoice", PW / 2, 18, { align: "center" });

  if (invoiceRef) {
    // "FBR INVOICE" pill
    fillRect(PILL_X, 5.5, PILL_W, 8, P.blue);
    doc.setFont("Helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...P.white);
    doc.text("FBR INVOICE", PILL_X + PILL_W / 2, 10.8, { align: "center" });

    // Invoice ref and scan hint below pill
    doc.setFont("helvetica", "normal");
    doc.setFontSize(5.8);
    doc.setTextColor(...P.blueFade);
    doc.text(`Ref # ${invoiceRef}`, PILL_X + PILL_W / 2, 18, {
      align: "center",
    });
    doc.setFontSize(5.5);
    doc.text("Scan QR code to verify", PILL_X + PILL_W / 2, 22, {
      align: "center",
    });

    // QR Code (far right)
    const qrDataUrl = await QRCode.toDataURL(invoiceRef, {
      width: 140,
      margin: 1,
    });
    doc.addImage(qrDataUrl, "PNG", QR_X, 1.2, QR_S, QR_S);
  }

  y = HDR_H + 3;

  // ─── INFO CARDS ──────────────────────────────────────────────────────────
  const CARD_HH = 5.5; // header strip height
  const CARD_GAP = 3;
  const COL_W = (PW - M * 2 - CARD_GAP * 2) / 3;
  const LABEL_W = 14.5;
  const VAL_W = COL_W - LABEL_W - 4.5;
  const ROW_GAP = 4.3;
  const LINE_H = 2.6; // wrapped value line height at 6.5pt
  const cardTop = y;
  const firstRowY = cardTop + CARD_HH + 4;

  const cards = [
    {
      x: M,
      title: "SELLER INFORMATION",
      rows: [
        ["Name:", companyName],
        ["NTN/CNIC:", data?.companyId?.ntn],
        ["Province:", data?.companyId?.province],
        ["Address:", data?.companyId?.businessAddress],
        // Contact / Landline only when the company has them - never a label
        // with nothing after it.
        ...sellerPhoneRows(data?.companyId),
      ],
    },
    {
      x: M + COL_W + CARD_GAP,
      title: "PURCHASER INFORMATION",
      rows: [
        ["Name:", data?.buyerId?.businessName],
        ["NTN/CNIC:", data?.buyerId?.registrationNo],
        ["Province:", data?.buyerId?.province],
        ["Address:", data?.buyerId?.address],
      ],
    },
    {
      x: M + (COL_W + CARD_GAP) * 2,
      title: "INVOICE DETAILS",
      rows: [
        ["Invoice #:", invoiceNo],
        ["Date:", moment(data?.date).format("DD-MMM-YYYY")],
        ["Tax Period:", moment(data?.date).format("YYYY-MM")],
        ["Type:", data?.invoiceType],
        ...(data?.purchaseOrderNo ? [["PO #:", data?.purchaseOrderNo]] : []),
      ],
    },
  ];

  // Cards are narrow here, so long names / addresses wrap and the rows below
  // them move down instead of printing over them; all three cards take the
  // height of the tallest one.
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.5);
  let contentH = 0;
  cards.forEach((card) => {
    let ry = 0;
    card.layout = card.rows.map(([label, value]) => {
      const lines = doc.splitTextToSize(String(value || "–"), VAL_W);
      const at = ry;
      ry += ROW_GAP + (lines.length - 1) * LINE_H;
      return { label, lines, at };
    });
    contentH = Math.max(contentH, ry - ROW_GAP);
  });
  const CARD_H = CARD_HH + 4 + contentH + 2.5;

  cards.forEach((card) => {
    strokeRect(card.x, cardTop, COL_W, CARD_H, P.border, P.offWhite);
    fillRect(card.x, cardTop, COL_W, CARD_HH, P.navy);

    doc.setFont("Helvetica", "bold");
    doc.setFontSize(6.5);
    doc.setTextColor(...P.white);
    doc.text(card.title, card.x + COL_W / 2, cardTop + 3.8, {
      align: "center",
    });

    card.layout.forEach(({ label, lines, at }) => {
      doc.setFont("Helvetica", "bold");
      doc.setFontSize(6);
      doc.setTextColor(...P.textMuted);
      doc.text(label, card.x + 2.5, firstRowY + at);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(6.5);
      doc.setTextColor(...P.textDark);
      doc.text(lines, card.x + 2.5 + LABEL_W, firstRowY + at);
    });
  });

  y = cardTop + CARD_H + 3;

  // ─── SECTION LABEL before table ──────────────────────────────────────────
  fillRect(M, y, PW - M * 2, 5.5, P.navy);
  doc.setFont("Helvetica", "bold");
  doc.setFontSize(6.5);
  doc.setTextColor(...P.white);
  doc.text("INVOICE ITEMS", M + 3, y + 3.8);

  y += 5.5;

  // ─── ITEMS TABLE ─────────────────────────────────────────────────────────
  const columns = [
    "#",
    "HS Code",
    "Product Description",
    "Sale Type",
    "Qty",
    "UOM",
    "Unit Rate",
    "Tax %",
    "Value Excl.",
    "Retail",
    "ST",
    "Extra",
    "Further",
    "FED",
    "WHT",
    "Disc.",
    "SRO",
    "SRO Item",
    "Amount",
  ];

  const rows = (data?.items || []).map((item, i) => [
    i + 1,
    item.hsCode || "",
    item?.itemId?.name || item?.productDescription || "",
    item?.saleType || "",
    item?.quantity || 0,
    item.uoM || "",
    formatTwoDecimals(
      (item.valueSalesExcludingST || 0) / (item?.quantity || 1),
    ),
    item.rate || 0,
    formatTwoDecimals(item.valueSalesExcludingST),
    formatTwoDecimals(item.fixedNotifiedValueOrRetailPrice),
    formatTwoDecimals(item.salesTaxApplicable),
    formatTwoDecimals(item.extraTax),
    formatTwoDecimals(item.furtherTax),
    formatTwoDecimals(item.fedPayable),
    formatTwoDecimals(item.salesTaxWithheldAtSource),
    formatTwoDecimals(item.discount),
    item.sroScheduleNo || "",
    item.sroItemSerialNo || "",
    formatTwoDecimals(item.totalValues),
  ]);

  autoTable(doc, {
    startY: y,
    head: [columns],
    body: rows,
    theme: "grid",
    styles: {
      fontSize: 6.5,
      cellPadding: { top: 1.2, bottom: 1.2, left: 0.8, right: 0.8 },
      font: "helvetica",
      textColor: P.textDark,
      lineColor: P.border,
      lineWidth: 0.1,
      overflow: "ellipsize",
      valign: "middle",
    },
    headStyles: {
      fillColor: P.blue,
      textColor: P.white,
      halign: "center",
      fontStyle: "bold",
      fontSize: 6,
      // Headers wrap onto two lines rather than being cut to "Ta..."
      overflow: "linebreak",
      cellPadding: { top: 1.4, bottom: 1.4, left: 0.5, right: 0.5 },
    },
    alternateRowStyles: { fillColor: P.offWhite },
    bodyStyles: { fillColor: P.white },
    // Text columns the Default invoice already shortens keep a fixed width.
    // The description and every amount column size to their content, so
    // amounts are not cut off and the description wraps in whatever room is
    // left.
    columnStyles: {
      0: { halign: "center", cellWidth: 5 },
      1: { cellWidth: 12.5 },
      2: { overflow: "linebreak", minCellWidth: 22 },
      3: { cellWidth: 12 },
      4: { halign: "right" },
      5: { cellWidth: 9 },
      6: { halign: "right" },
      7: { halign: "right", cellWidth: 8 },
      8: { halign: "right" },
      9: { halign: "right" },
      10: { halign: "right" },
      11: { halign: "right" },
      12: { halign: "right" },
      13: { halign: "right" },
      14: { halign: "right" },
      15: { halign: "right" },
      16: { cellWidth: 9 },
      17: { cellWidth: 9 },
      18: { halign: "right", fontStyle: "bold" },
    },
    pageBreak: "auto",
    rowPageBreak: "avoid",
    showHead: "everyPage",
    // The bottom margin is measured from the foot of the A4 sheet, so the
    // table breaks just above the footer bar of the top half.
    margin: {
      left: M,
      right: M,
      top: CONT_TOP,
      bottom: SHEET_H - PH + FOOTER_BAR_H + 3,
    },
  });

  // ─── SUMMARY + WORDS SECTION ─────────────────────────────────────────────
  y = doc.lastAutoTable.finalY + 3;

  const SUM_W = 64;
  const SUM_X = PW - M - SUM_W;
  const WORD_W = SUM_X - M - 3;

  // Discount is deducted after the sales tax, which is charged on the
  // undiscounted value - so it never changes the "Sales Tax:" line above it.
  // The row is only printed when something was actually discounted.
  const totalDiscount = (data?.items || []).reduce(
    (sum, item) => sum + (Number(item?.discount) || 0),
    0,
  );

  const summaryRows = [
    { label: "Excl. Sales Tax Value", value: data?.subTotal || 0, grand: false },
    { label: "Sales Tax:", value: data?.taxTotal || 0, grand: false },
    ...(totalDiscount > 0
      ? [{ label: "Discount:", value: -totalDiscount, grand: false }]
      : []),
    { label: "Inclusive Sales Tax Value:", value: data?.grandTotal || 0, grand: true },
    // Section 236H comes after the invoice value and is never part of it; the
    // final amount is that value plus the 236H amount. Both are left out when
    // the invoice has no 236H rate.
    ...(hasSection236H(data?.GSection236H)
      ? [
          {
            label: `${section236HLabel(data?.GSection236H)}:`,
            value: section236HAmountFor(data?.grandTotal, data?.GSection236H),
            grand: false,
          },
          {
            label: `${SECTION_236H_FINAL_LABEL}:`,
            value: section236HFinalAmountFor(
              data?.grandTotal,
              data?.GSection236H,
            ),
            grand: true,
          },
        ]
      : []),
  ];

  const SUM_HDR_H = 5.5;
  const SUM_ROW_H = 5.5;
  const SUM_H = SUM_HDR_H + summaryRows.length * SUM_ROW_H + 0.5;

  needsNewPage(SUM_H + 7);

  // Summary card
  strokeRect(SUM_X, y, SUM_W, SUM_H, P.border, P.white);
  fillRect(SUM_X, y, SUM_W, SUM_HDR_H, P.navy);

  doc.setFont("Helvetica", "bold");
  doc.setFontSize(6.5);
  doc.setTextColor(...P.white);
  doc.text("INVOICE SUMMARY", SUM_X + SUM_W / 2, y + 3.8, { align: "center" });

  summaryRows.forEach((row, i) => {
    const ry = y + SUM_HDR_H + i * SUM_ROW_H;
    if (row.grand) {
      fillRect(SUM_X, ry, SUM_W, SUM_ROW_H, P.grandTotalBg);
      doc.setFont("Helvetica", "bold");
      doc.setFontSize(7);
      doc.setTextColor(...P.white);
    } else {
      hLine(SUM_X + 1.5, SUM_X + SUM_W - 1.5, ry);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(6.5);
      doc.setTextColor(...P.textDark);
    }
    doc.text(row.label, SUM_X + 2.5, ry + 3.7);
    doc.text(
      String(displayTwoDecimalDigits(row.value)),
      SUM_X + SUM_W - 2.5,
      ry + 3.7,
      { align: "right" },
    );
  });

  // Amount in words card (aligned with summary)
  strokeRect(M, y, WORD_W, SUM_H, P.border, P.lightBlue);
  fillRect(M, y, WORD_W, SUM_HDR_H, P.navy);

  doc.setFont("Helvetica", "bold");
  doc.setFontSize(6.5);
  doc.setTextColor(...P.white);
  doc.text("AMOUNT IN WORDS", M + 3, y + 3.8);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...P.textDark);

  let amountWords = "N/A";
  try {
    amountWords = `${toWords(Math.floor(data?.grandTotal || 0)).toUpperCase()} RUPEES ONLY`;
  } catch (_) {
    /* fallback already set */
  }

  const wordLines = doc.splitTextToSize(amountWords, WORD_W - 6);
  doc.text(wordLines, M + 3, y + SUM_HDR_H + 4.5);

  y += SUM_H + 3.5;
  needsNewPage(6);

  // ─── FOOTER NOTE ─────────────────────────────────────────────────────────
  hLine(M, PW - M, y, P.border);
  y += 3;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6);
  doc.setTextColor(...P.textMuted);
  doc.text(
    "This is a computer generated invoice. No signature is required.",
    PW / 2,
    y,
    { align: "center" },
  );

  // The last page's footer bar follows the note directly, leaving no empty
  // band above it; pages the table fills keep theirs at the foot of the half.
  const lastFooterY = y + 3;

  // ─── CONTINUATION STRIP + FOOTER BAR (stamped on every page) ─────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let pg = 1; pg <= totalPages; pg++) {
    doc.setPage(pg);
    if (pg > 1) {
      fillRect(0, 0, PW, CONT_BAND_H, P.navy);
      fillRect(0, CONT_BAND_H, PW, 1, P.blue);
      doc.setFont("Helvetica", "bold");
      doc.setFontSize(7.5);
      doc.setTextColor(...P.white);
      doc.text("Sale Tax Invoice", M, 5.3);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(6);
      doc.setTextColor(...P.blueFade);
      doc.text(
        `Invoice # ${invoiceNo}  ·  continued from page ${pg - 1}`,
        PW - M,
        5.3,
        { align: "right" },
      );
    }
    const barY = pg === totalPages ? lastFooterY : PH - FOOTER_BAR_H;
    fillRect(0, barY, PW, FOOTER_BAR_H, P.navy);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(5.5);
    doc.setTextColor(...P.blueFade);
    doc.text(`${companyName}  ·  FBR Ref: ${invoiceRef}`, M, barY + 3.8);
    doc.text(`Page ${pg} of ${totalPages}`, PW - M, barY + 3.8, {
      align: "right",
    });
  }

  return doc;
};

export const generateHalfPageInvoice = async (data) => {
  const doc = await buildHalfPageInvoice(data);

  // ─── OUTPUT ───────────────────────────────────────────────────────────────
  const pdfBlob = doc.output("blob");
  window.open(URL.createObjectURL(pdfBlob));
};
